import React, { useMemo, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import {
  useGetInvoiceReservationsListQuery,
  useGetInvoiceReservationWithAssociationsByIdQuery,
} from '../../Service/reservationsLookupApi';
import { useDebounce } from '../../../Shared/Hooks/useDebounce';
import type {
  ReservationOption,
  ReservationAutocompleteProps,
} from './ReservationAutocomplete.types';
import { buildReservationOption } from './reservationOption';

export const RESERVATION_SEARCH_MIN_LENGTH = 3;
export const RESERVATION_SEARCH_DEBOUNCE_MS = 300;

const ReservationAutocomplete: React.FC<ReservationAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
  disabled = false,
}) => {
  // inputValue is what the field displays; searchText is only what the user typed.
  // Keeping them apart stops the selected option's label from being sent as a search term.
  const [inputValue, setInputValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebounce(searchText.trim(), RESERVATION_SEARCH_DEBOUNCE_MS);
  const hasSearchTerm = debouncedSearch.length >= RESERVATION_SEARCH_MIN_LENGTH;

  const { data: reservationsPaged, isFetching } = useGetInvoiceReservationsListQuery(
    {
      pageNumber: 1,
      pageSize: 100,
      searchTerm: hasSearchTerm ? debouncedSearch : undefined,
    },
    {
      skip: debouncedSearch.length > 0 && !hasSearchTerm,
    }
  );

  // Load selected reservation separately to always have it in options
  const { data: selectedReservation } = useGetInvoiceReservationWithAssociationsByIdQuery(value!, {
    skip: !value,
  });

  const listOptions = useMemo<ReservationOption[]>(
    () => (reservationsPaged?.items ?? []).map(buildReservationOption),
    [reservationsPaged?.items]
  );

  // Built only from the lookup result, so new search results never change its reference.
  // MUI treats a new value reference as a value change and would reset what the user is typing.
  const lookedUpOption = useMemo<ReservationOption | null>(
    () => (selectedReservation ? buildReservationOption(selectedReservation) : null),
    [selectedReservation]
  );

  const selectedOption = useMemo<ReservationOption | null>(() => {
    if (value == null) {
      return null;
    }

    return lookedUpOption?.id === value
      ? lookedUpOption
      : (listOptions.find((o) => o.id === value) ?? null);
  }, [value, lookedUpOption, listOptions]);

  // Keep the selected reservation in the options even when the search results omit it
  const options = useMemo<ReservationOption[]>(
    () =>
      selectedOption && !listOptions.some((o) => o.id === selectedOption.id)
        ? [selectedOption, ...listOptions]
        : listOptions,
    [selectedOption, listOptions]
  );

  const handleInputChange = (_: React.SyntheticEvent, val: string, reason: string) => {
    setInputValue(val);
    if (reason === 'input') {
      setSearchText(val);
    } else {
      // 'reset' (option selected / value changed from outside) and 'clear'
      setSearchText('');
    }
  };

  return (
    <Autocomplete<ReservationOption>
      options={options}
      loading={isFetching}
      value={selectedOption}
      disabled={disabled}
      onChange={(_, val) => onChange(val?.id)}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.label}
      loadingText="Szukam…"
      noOptionsText={
        searchText.trim().length < RESERVATION_SEARCH_MIN_LENGTH
          ? `Wpisz min. ${RESERVATION_SEARCH_MIN_LENGTH} znaki`
          : 'Brak wyników'
      }
      renderOption={(props, o) => {
        const { key, ...restProps } = props as any;
        return (
          <li key={o.id} {...restProps}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {o.label}
              </Typography>
              {o.meta && (
                <Typography variant="caption" color="text.secondary">
                  {o.meta}
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Rezerwacja"
          placeholder="Szukaj po ofercie, terminie, nabywcy, osobie…"
          error={!!error}
          helperText={helperText ?? 'Formularz zapisuje tylko ID rezerwacji'}
          fullWidth
        />
      )}
      sx={{
        '& .MuiAutocomplete-input': {
          minWidth: '200px !important',
        },
      }}
    />
  );
};

export default ReservationAutocomplete;
